import { create } from 'zustand';
import { 
  getCollectionSites, 
  createCollectionSite, 
  updateCollectionSite, 
  deleteCollectionSite,
  getCollectionSite
} from '../services/collection-site.service';
import { 
  CollectionSiteSchema, 
  CollectionSiteRequest, 
  UpdateCollectionSiteRequest, 
  CollectionSiteQueryParams,
  PaginatedCollectionSiteResponse 
} from '../types/collection-site.type';
import { AxiosError } from 'axios';

interface CollectionSiteState {
  sites: CollectionSiteSchema[];
  selectedSite: CollectionSiteSchema | null;
  total: number;
  isLoading: boolean;
  error: string | null;
  showSiteModal: boolean;
  setShowSiteModal: (show: boolean) => void;
  getCollectionSites: (params: CollectionSiteQueryParams) => Promise<PaginatedCollectionSiteResponse | null>;
  getCollectionSite: (siteId: string) => Promise<CollectionSiteSchema | null>;
  createCollectionSite: (site: CollectionSiteRequest) => Promise<CollectionSiteSchema | null>;
  updateCollectionSite: (siteId: string, site: UpdateCollectionSiteRequest) => Promise<CollectionSiteSchema | null>;
  deleteCollectionSite: (siteId: string) => Promise<boolean>;
}

interface GenericResponse<T> {
  error?: {
    message?: string;
    code?: string;
  } | null;
  response?: {
    message?: string;
    code?: string;
  } | null;
  result: T;
}

export const useCollectionSiteStore = create<CollectionSiteState>((set) => ({
  sites: [],
  selectedSite: null,
  total: 0,
  isLoading: false,
  error: null,
  showSiteModal: false,
  setShowSiteModal: (show: boolean) => set({ showSiteModal: show }),

  getCollectionSites: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getCollectionSites(params);
      const data = response as GenericResponse<CollectionSiteSchema[]>;
      
      set({ 
        sites: data.result,
        total: data.result.length,
        isLoading: false 
      });

      return {
        sites: data.result,
        total: data.result.length
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        set({ error: error.response?.data?.["detail"] || 'Failed to get collection sites', isLoading: false });
      } else {
        set({ error: 'Failed to get collection sites', isLoading: false });
      }
      return null;
    }
  },

  getCollectionSite: async (siteId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getCollectionSite(siteId);
      const data = response as GenericResponse<CollectionSiteSchema>;
      
      set({ 
        selectedSite: data.result,
        isLoading: false 
      });

      return data.result;
    } catch (error) {
      if (error instanceof AxiosError) {
        set({ error: error.response?.data?.["detail"] || 'Failed to get collection site', isLoading: false });
      } else {
        set({ error: 'Failed to get collection site', isLoading: false });
      }
      return null;
    }
  },

  createCollectionSite: async (site) => {
    set({ isLoading: true, error: null });
    try {
      const response = await createCollectionSite(site);
      const data = response as GenericResponse<CollectionSiteSchema>;
      
      set((state) => ({ 
        sites: [...state.sites, data.result],
        isLoading: false 
      }));

      return data.result;
    } catch (error) {
      if (error instanceof AxiosError) {
        set({ error: error.response?.data?.["detail"] || 'Failed to create collection site', isLoading: false });
      } else {
        set({ error: 'Failed to create collection site', isLoading: false });
      }
      return null;
    }
  },

  updateCollectionSite: async (siteId, site) => {
    set({ isLoading: true, error: null });
    try {
      const response = await updateCollectionSite(siteId, site);
      const data = response as GenericResponse<CollectionSiteSchema>;
      
      set((state) => ({ 
        sites: state.sites.map(s => s.id === siteId ? data.result : s),
        selectedSite: state.selectedSite?.id === siteId ? data.result : state.selectedSite,
        isLoading: false 
      }));

      return data.result;
    } catch (error) {
      if (error instanceof AxiosError) {
        set({ error: error.response?.data?.["detail"] || 'Failed to update collection site', isLoading: false });
      } else {
        set({ error: 'Failed to update collection site', isLoading: false });
      }
      return null;
    }
  },

  deleteCollectionSite: async (siteId) => {
    set({ isLoading: true, error: null });
    try {
      await deleteCollectionSite(siteId);
      
      set((state) => ({ 
        sites: state.sites.filter(s => s.id !== siteId),
        selectedSite: state.selectedSite?.id === siteId ? null : state.selectedSite,
        isLoading: false 
      }));

      return true;
    } catch (error) {
      if (error instanceof AxiosError) {
        set({ error: error.response?.data?.["detail"] || 'Failed to delete collection site', isLoading: false });
      } else {
        set({ error: 'Failed to delete collection site', isLoading: false });
      }
      return false;
    }
  },
})); 